import { Optional, Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { ViewChild, ElementRef, forwardRef, HostBinding } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AbstractControl, FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { ControlContainer, ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { Subscription } from 'rxjs';

import { AppConstants } from '@unpispas/upp-defs';
import { GenericUtils } from '@unpispas/upp-defs';
import { adhocService } from '@unpispas/upp-base';
import { languageSupport, languageService } from '@unpispas/upp-base';
import { httpService } from '@unpispas/upp-base';
import { preloadService } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';

type UppIImageSource = 'avatar' | 'upload' | 'search';
type UppAvatarMode = 'min' | 'full';

/**
 * The `UppModalCropComponent` is a modal component used for cropping images.
 * It is designed to work within an Ionic modal context and accepts an image and its desired dimensions.
 *
 * @remarks
 * This component extends the `languageSupport` class, providing multi-language support.
 * It interacts with Ionic's `ModalController` for presenting and dismissing the modal.
 *
 * @selector `upp-modal-crop-image`
 * @templateUrl `./upp-modal.html`
 * @styleUrls `./upp-modal.scss`
 *
 * @example
 * ```typescript
 * const modal = await this.modalCtrl.create({
 *     component: UppModalCropComponent,
 *     componentProps: {
 *         image: imageData,
 *         height: 200,
 *         width: 200
 *     }
 * });
 * 
 * modal.onDidDismiss().then((result) => {
 *     console.log('Cropped Image:', result.data);
 * });
 * ```
 */
@Component({
    selector: 'upp-modal-crop-image',
    templateUrl:'./upp-modal.html',
    styleUrls: [ 
        './upp-modal.scss'
    ]
})
export class UppModalCropComponent extends languageSupport implements OnDestroy {
    /** The image to be cropped. */
    @Input() image: any = null;    
    /** The desired height of the cropped image. */
    @Input() height = 0;
    /** The desired width of the cropped image. */
    @Input() width = 0;
    
    /**
     * Indicates if the component has fully loaded.
     * @defaultValue `false`
     */    
    public _loaded = false;

    /**
     * Constructs the `UppModalCropComponent` and injects necessary dependencies.
     * @param lang Language service for multi-language support.
     * @param modalCtrl Controller for managing the modal.
     * @param params Service for accessing modal parameters.
     * @param view View service for UI-related functionalities.
     */    
    constructor(private lang: languageService, private modalCtrl: ModalController, public view: viewService) {
        super(lang, null);
    }

    /**
     * Cleans up resources when the modal is destroyed.
     */    
    ngOnDestroy(){
        super.OnDestroy();
    }

    /**
     * Dismisses the modal with the cropped image data.
     * @param data The cropped image data.
     */    
    async OnCrop(data: any){
        (document.activeElement as HTMLElement)?.blur();
        await this.modalCtrl.dismiss(data);
    }
    
    /**
     * Closes the modal without returning any data.
     */    
    async CloseModal() {
        (document.activeElement as HTMLElement)?.blur();
        await this.modalCtrl.dismiss(null);
    }        
}

/**
 * The `UppImageComponent` is a versatile image component with cropping capabilities, lazy loading,
 * and base64 image transformation. It is compatible with Angular's reactive forms as a `ControlValueAccessor`.
 *
 * @remarks
 * This component supports both readonly and editable modes.
 * It uses a modal-based cropping interface (`UppModalCropComponent`) to allow users to crop images before upload.
 *
 * @selector `upp-image`
 * @templateUrl `./upp-image.html`
 * @styleUrls `./upp-image.scss`
 * @providers `NG_VALUE_ACCESSOR`
 * @changeDetection `ChangeDetectionStrategy.OnPush`
 *
 * @example
 * ```html
 * <upp-image
 *     [readonly]="false"
 *     [src]="imageSource"
 *     [crheight]="200"
 *     [crwidth]="200"
 *     (Changed)="onImageChange($event)">
 * </upp-image>
 * ```
 */
@Component({
    selector: 'upp-image',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./upp-image.html',
    styleUrls: [ 
        './upp-image.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppImageComponent),
            multi: true
        }            
    ]
})
export class UppImageComponent extends languageSupport implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    /**
     * Determines if the component is in readonly mode.
     * @defaultValue `true`
     */
    @Input() readonly = true;
    @Input() mode: 'upload' | 'config' = 'upload';
    @Input() search: string | null = null;

    /** Flex layout with expandable config panel (mode=config only). */
    @HostBinding('class.config-layout')
    get configLayout(): boolean {
        return this.mode === 'config';
    }

    /** Panel open: parent .img-box can grow (see upp-form / upp-image styles). */
    @HostBinding('class.config-expanded')
    get configExpanded(): boolean {
        return this.mode === 'config' && this.ShowConfig;
    }

    /** 
     * The name of the form control this input is bound to. 
     * @defaultValue `''`
    */
    @Input() formControlName = '';

    /**
     * The source of the image. Can be a URL or a base64 string.
     * @defaultValue `null`
     */    
    @Input() src: string | null = null;

    /**
     * The height constraint for cropping.
     * @defaultValue `'0'`
     */    
    @Input() crheight = '0';

    /**
     * The width constraint for cropping.
     * @defaultValue `'0'`
     */
    @Input() crwidth = '0';
    
    /**
     * Tracks the image loading mode (`lazy` or `eager`).
     * @defaultValue `'lazy'`
     */    
    public _loading = 'lazy';

    /** optional FromGroup */
    public formGroup: FormGroup | null = null
    public formControl: AbstractControl | null = null;
        
    /* eslint-disable @typescript-eslint/no-unused-vars */
    /** Callback to notify Angular Forms when the value changes. */
    private _onChangeCallback = (v: any) => {
        // nothing to do (required by angular)
    }
    /** Callback to notify Angular Forms when the input is touched. */
    private _onTouchCallback = () => {
        // nothing to do (required by angular)
    }
    /* eslint-enable @typescript-eslint/no-unused-vars */          

    /**
     * Image element reference in the template.
     */    
    @ViewChild('image', {static: false}) _image: ElementRef | null = null;
    @ViewChild('fileUploader', { static: false }) fileUploader: ElementRef | null = null;

    /**
     * Determines if the image source requires the `crossorigin` attribute for server images.
     *
     * @remarks
     * The `_cors` getter checks if the `src` is an HTTP URL that is external to the base URL.
     * This is used to handle cross-origin requests for server-hosted images.
     *
     * @returns `true` if the `crossorigin` attribute should be used; otherwise, `false`.
     */    
    get _cors(): boolean{    // use crossorigin for server images
        if (this.src){
            return !this.src.startsWith('http') || (!!this.src && (this.src.startsWith(AppConstants.baseURL)));
        }

        return false;
    }

    private b64: string | null = null;

    /**
     * Private accessor for the base64-encoded value of the image.
     *
     * @remarks
     * This getter retrieves the stored base64 representation of the image (`b64`).
     * It is used internally for managing the image's state.
     *
     * @returns The base64 string of the image, or `null` if not available.
     */    
    private get _value(){
        return this.b64;
    }

    /**
     * Private setter for the base64-encoded value of the image.
     *
     * @remarks
     * This setter updates the base64 representation (`b64`) and synchronizes it with
     * the `src` property. It also triggers change detection to update the UI.
     *
     * @param value The new base64 value for the image.
     */    
    private set _value(value){
        this.b64 = value; 
        this.src = value;
        this.change.markForCheck();
    }
    
    private _cachesrc: SafeUrl | null = null;

    /**
     * Gets the current source of the image, which may be the base64 value, a cached value,
     * or the original source URL.
     *
     * @returns The effective source of the image (`SafeUrl`, string, or `null`).
     */    
    get _source(): SafeUrl | string | null{
        if (this.ShowConfig && this._imagesource){
            return this._imagesource;
        }
        if (this._value || this._cachesrc){
            return this._value || this._cachesrc;
        }
        // Defer remote URL until near-viewport (lazy) so grids do not fetch every tile at once.
        if (!this._networkAllowed){
            return null;
        }
        return this.src;
    }

    /**
     * Sets the current source of the image and ensures it is sanitized if necessary.
     *
     * @remarks
     * This setter uses Angular's `DomSanitizer` to handle potentially unsafe URLs.
     * It stores the sanitized or safe value in `_cachesrc` and triggers change detection.
     *
     * @param value The new source of the image, which can be a URL, a base64 string, or `SafeUrl`.
     */    
    private set _source(value: string | SafeUrl | null){
        const _isSafeUrl = (value: string | SafeUrl): value is SafeUrl => {
            return typeof value === 'object' && value !== null && ('changingThisBreaksApplicationSecurity' in value);
        }

        if (value){
            this._cachesrc = _isSafeUrl(value) ? value : this.sanitizer.bypassSecurityTrustUrl(value);
        }

        this.change.markForCheck();
    }

    private c64: any = null;

    /**
     * Retrieves the base64-encoded string of the image.
     *
     * @remarks
     * The `Base64` getter uses a `Promise` to asynchronously generate a base64-encoded
     * string from the image source. If the image is already processed, it returns the cached value (`c64`).
     *
     * @returns A `Promise` that resolves to the base64 string of the image, or `null` if processing fails.
     */    
    get Base64() {
        return new Promise((resolve) =>{
            if (!this.c64){
                const _img = new Image();
                _img.onload = () => {
                    const _cnv = document.createElement("canvas");
                    const _ctx = _cnv.getContext("2d");
        
                    if (_cnv && _ctx) {
                        _cnv.width = _img.naturalWidth;
                        _cnv.height = _img.naturalHeight;
                        _ctx.drawImage(_img, 0, 0);
    
                        const _dataurl = _cnv.toDataURL("image/png");
                        this.c64 = _dataurl.split(',', 2)[1];
    
                        resolve(this.c64);      
                    }

                    resolve(null);
                }
    
                // Encode from the logical src (or inline value), not the deferred display URL.
                const _loadSrc = this._value || this.src;
                if (_loadSrc){
                    _img.crossOrigin = "Anonymous";
                    _img.src = _loadSrc;  
                }
                else {
                    resolve(null);
                }
            }
            else {
                resolve(this.c64);    
            }
        });
    }

    /**
     * Constructs the `UppImageComponent` and injects necessary dependencies.
     * 
     * @param lang Language service for multi-language support.
     * @param change Change detector for triggering view updates.
     * @param controlContainer Provides access to the form control's parent `FormGroup`, enabling integration with reactive forms.
     * @param http HTTP service for tracking online status and connectivity.
     * @param preload Preload service for caching and optimizing image loading.
     * @param sanitizer Sanitizer for handling and securing image URLs.
     * @param modalCtrl Controller for managing modal interactions, such as cropping images.
     * 
     * @remarks
     * - If the component is used inside a reactive form, it will attempt to retrieve its associated `FormControl` from the `FormGroup`.
     * - Implements `ControlValueAccessor` to allow two-way binding with Angular forms.
     * - Supports image preprocessing, caching, and transformations (e.g., EXIF orientation adjustments).
     */   
    constructor(
        private lang: languageService,
        private change: ChangeDetectorRef,
        @Optional() private controlContainer: ControlContainer,
        private http: httpService,
        private preload: preloadService,
        private sanitizer: DomSanitizer,
        private modalCtrl: ModalController,
        private adhoc: adhocService,
        private toast: toastService,
        private host: ElementRef<HTMLElement>,
    ){
        super(lang, change);
    }
    
    /**
     * Tracks whether the image has fully loaded.
     * @defaultValue `false`
     */        
    public _loaded = false;

    /**
     * Tracks the online status of the application.
     * @defaultValue `true`
     */    
    public _online = true;

    /**
     * Checks whether the current image source is cached.
     * @returns `true` if the image source is cached; otherwise, `false`.
     */    
    get IsCached(): boolean{
        if (this.src){
            return !!this.preload.Recover(this.src);
        }
        return true;
    }

    /**
     * Determines whether the image is visible.
     * Combines the `_loaded` state and whether the image is cached.
     * @returns `true` if the image is either loaded or cached; otherwise, `false`.
     */    
    get IsVisible(){
        return this._loaded || this.IsCached;
    }

    /**
     * Updates the `_loaded` state when the image finishes loading.
     * Triggers change detection to update the view.
     */    
    OnLoaded(){
        this._loaded = true;
        this.change.markForCheck();
    }

    private _online_subscription: Subscription | null = null;
    private _form_subscription: Subscription | null = null;

    /** True once remote network fetch is allowed (cache hit, inline src, eager, or near viewport). */
    private _networkAllowed = false;

    private _lazyObserver: IntersectionObserver | null = null;

    /**
     * Sets the online status and manages the online subscription.
     * @param value The new online status (`true` for online, `false` for offline).
     */    
    set Online(value: boolean){
        this._online = value;

        if (this._online && this._online_subscription){
            this._online_subscription.unsubscribe();
            this._online_subscription = null;
        }

        this.change.markForCheck();
    }

    /**
     * Initializes the component and subscribes to the online status.
     */    
    ngOnInit() {
        this._configForm = this.LoadConfigForm();

        this._online = this.http.IsOnline;
        if (!this._online){
            this._online_subscription = this.http.OnOnline.subscribe(
            data => {
                this.Online = data;
            });    
        }

        if (this.controlContainer && this.controlContainer instanceof FormGroupDirective){
            this.formGroup = this.controlContainer.form;
        }

        this.formControl = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (this.formControl){
            this.value = this.formControl.value || null;

            this._form_subscription = this.formControl.valueChanges.subscribe(
            () => {
                this.value = this.formControl?.value || null;
            });
        }         
    }

    /**
     * Handles changes to the input properties, such as the image source.
     *
     * Remote URLs with `_loading === 'lazy'` wait until the host is near the viewport
     * before enqueueing preload / exposing `src` on the `<img>`.
     */    
    ngOnChanges(){
        this._configForm = this.LoadConfigForm();

        this.c64 = null; 
        this.b64 = null;
        this._networkAllowed = false;
        this._cachesrc = null;
        this._StopLazyObserver();

        if (!this.src){
            this._loaded = true;
            this.ShowConfig = false;
            return;
        }

        if (this._IsInlineSrc(this.src)){
            this._networkAllowed = true;
            this.ShowConfig = false;
            return;
        }

        this._cachesrc = this.preload.Recover(this.src);
        if (this._cachesrc){
            this._networkAllowed = true;
            this.ShowConfig = false;
            return;
        }

        if (this._loading === 'lazy'){
            this._StartLazyObserver();
        }
        else {
            this._AllowNetworkLoad();
        }

        this.ShowConfig = false;
    }

    /**
     * Cleans up subscriptions and resources when the component is destroyed.
     */    
    ngOnDestroy(){
        super.OnDestroy();
        this._StopLazyObserver();

        if (this._online_subscription){
            this._online_subscription.unsubscribe();
            this._online_subscription = null;
        }

        if (this._form_subscription){
            this._form_subscription.unsubscribe();
            this._form_subscription = null;
        }
    }

    /**
     * Returns true when `src` does not need a network fetch.
     * @param src - Image source string.
     */
    private _IsInlineSrc(src: string): boolean {
        return /^data:image\//.test(src) || src.startsWith('blob:');
    }

    /**
     * Allows remote fetch and enqueues preload when not already cached.
     */
    private _AllowNetworkLoad(): void {
        if (this._networkAllowed){
            return;
        }

        this._networkAllowed = true;
        this._EnqueueRemoteSrc();
        this.change.markForCheck();
    }

    /**
     * Enqueues the current remote `src` into preloadService when missing from cache.
     */
    private _EnqueueRemoteSrc(): void {
        if (!this.src || this._IsInlineSrc(this.src)){
            return;
        }

        const cached = this.preload.Recover(this.src);
        if (cached){
            this._cachesrc = cached;
            return;
        }

        this.preload.Enqueue(this.src, (safeurl) => {
            if (safeurl){
                this._source = safeurl;
            }
            this.change.markForCheck();
        });
    }

    /**
     * Observes the host until it is near the viewport, then allows network load.
     * Falls back to immediate load when IntersectionObserver is unavailable.
     */
    private _StartLazyObserver(): void {
        if (typeof IntersectionObserver === 'undefined'){
            this._AllowNetworkLoad();
            return;
        }

        const element = this.host?.nativeElement;
        if (!element){
            this._AllowNetworkLoad();
            return;
        }

        this._lazyObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries){
                    if (entry.isIntersecting){
                        this._AllowNetworkLoad();
                        this._StopLazyObserver();
                        break;
                    }
                }
            },
            { root: null, rootMargin: '100px 0px', threshold: 0.01 }
        );
        this._lazyObserver.observe(element);
    }

    /**
     * Disconnects the lazy-load IntersectionObserver when present.
     */
    private _StopLazyObserver(): void {
        if (this._lazyObserver){
            this._lazyObserver.disconnect();
            this._lazyObserver = null;
        }
    }
    
    /**
     * Clears this image source
     */       
    fileDelete(){
        this.value = null;        
    }

    /**
     * Converts a blob to a data URL suitable for canvas-based cropping.
     * @param blob Image bytes to encode.
     * @returns A data URL or `null` when encoding fails.
     * @private
     */
    private _blobToDataUrl(blob: Blob): Promise<string | null> {
        return new Promise((resolve) => {
            const _reader = new FileReader();
            _reader.onload = () => {
                resolve(typeof _reader.result === 'string' ? _reader.result : null);
            };
            _reader.onerror = () => {
                resolve(null);
            };
            _reader.readAsDataURL(blob);
        });
    }

    /**
     * Builds a data URL from the image already rendered in the preview `<img>`.
     * @returns A data URL when the preview is loaded and canvas-safe, otherwise `null`.
     * @private
     */
    private _loadedPreviewToDataUrl(): string | null {
        const _el = this._image?.nativeElement as HTMLImageElement | undefined;
        if (!_el?.complete || _el.naturalWidth === 0 || _el.naturalHeight === 0){
            return null;
        }

        const _cnv = document.createElement('canvas');
        const _ctx = _cnv.getContext('2d');
        if (!_cnv || !_ctx){
            return null;
        }

        _cnv.width = _el.naturalWidth;
        _cnv.height = _el.naturalHeight;
        try {
            _ctx.drawImage(_el, 0, 0);
            return _cnv.toDataURL('image/png');
        }
        catch {
            return null;
        }
    }

    /**
     * Normalizes an image source to a data URL so ngx-img-cropper can read pixels safely.
     * Prefers the preview already on screen, then cross-origin image load and fetch without credentials.
     * @param _imagesrc Raw image source from the component value or preview.
     * @returns A crop-safe data URL or `null` when the source cannot be loaded.
     * @private
     */
    private async _toCropSafeSource(_imagesrc: string): Promise<string | null> {
        const _src = String(_imagesrc || '').trim();
        if (_src === ''){
            return null;
        }

        if (_src.startsWith('data:')){
            return _src;
        }

        const _fromPreview = this._loadedPreviewToDataUrl();
        if (_fromPreview){
            return _fromPreview;
        }

        if (_src.startsWith('http://') || _src.startsWith('https://') || _src.startsWith('/')){
            try {
                return await GenericUtils.imageTobase64(_src);
            }
            catch {
                // fall back to anonymous fetch when canvas image load is unavailable
            }

            try {
                const _response = await fetch(_src, { credentials: 'omit' });
                if (_response.ok){
                    const _dataUrl = await this._blobToDataUrl(await _response.blob());
                    if (_dataUrl){
                        return _dataUrl;
                    }
                }
            }
            catch {
                return null;
            }
        }

        return null;
    }

    /**
     * Loads a data URL into a fully decoded image element for the cropper.
     * @param dataUrl Crop-safe data URL.
     * @returns A loaded image element, or `null` when decoding fails.
     * @private
     */
    private _dataUrlToImage(dataUrl: string): Promise<HTMLImageElement | null> {
        return new Promise((resolve) => {
            const _img = new Image();
            _img.onload = () => {
                resolve(_img);
            };
            _img.onerror = () => {
                resolve(null);
            };
            _img.src = dataUrl;
        });
    }

    /**
     * Opens a modal for cropping the image.
     * @param _imagesrc The image source to crop.
     * @returns A promise that resolves to the cropped image or `null` if cancelled.
     * @private
     */    
    private async _showCropModal(_imagesrc: any): Promise<string | null> {
        const _safeSrc = await this._toCropSafeSource(String(_imagesrc || ''));
        if (!_safeSrc){
            this.toast.ShowAlert('warning', this.tr('@image_config_search_error'));
            return null;
        }

        const _image = await this._dataUrlToImage(_safeSrc);
        if (!_image){
            this.toast.ShowAlert('warning', this.tr('@image_config_search_error'));
            return null;
        }

        const modal = await this.modalCtrl.create({ 
            component: UppModalCropComponent,
            cssClass: 'modal-crop',
            componentProps: { 
                image: _image,
                height: Number(this.crheight),
                width: Number(this.crwidth),
            }
        });
    
        await modal.present();
        const { data } = await modal.onDidDismiss();

        return data || null;
    }

    /**
     * Emits an event when the image is changed.
     */    
    @Output() Changed = new EventEmitter<any>();         

    /**
     * Emits the `Changed` event when an image is modified.
     * @param event The change event from the file input.
     */    
    async OnChange(event: Event){
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]){
            const _dataUrl = await this._readImage(input.files[0]);
            const _result = await this._showCropModal(_dataUrl);
            input.value = '';

            if (_result !== null){
                this.value = _result;
            }
            return;
        }

        input.value = '';
    }

    /**
     * Handles the primary image action from the preview overlay.
     *
     * @remarks
     * In `config` mode this toggles the configuration dropdown.
     * In `upload` mode this opens the native file picker.
     *
     * @param event Optional DOM event to cancel default bubbling behavior.
     */
    OnClick(event: Event | null = null): void {
        if (event){
            event.preventDefault();
            event.stopPropagation();
        }

        if (this.mode === 'config'){
            this.OnShowConfig();
            return;
        }

        this.OnUpload();
    }

    /**
     * Opens the hidden file input used to upload a local image.
     */
    OnUpload() {
        if (this.fileUploader?.nativeElement){
            (this.fileUploader.nativeElement as HTMLInputElement).click();
        }
    }

    /************************************************/
    /* image configuration options                  */
    /************************************************/

    @ViewChild('configUploader', { static: false }) configUploader: ElementRef | null = null;

    /**
     * Indicates whether avatar generation action is allowed.
     */
    get CanAvatar(): boolean {
        return this._ValidText && this._ValidColor;
    }

    /**
     * Indicates whether internet search action is allowed.
     */
    get CanSearch(): boolean {
        return this._ValidText;
    }

    /**
     * Whether the current preview/value can be opened in the crop modal.
     * Requires positive `crwidth`/`crheight` and a non-empty image URL or data URL.
     */
    get CanCrop(): boolean {
        const _h = Number(this.crheight);
        const _w = Number(this.crwidth);
        if (!(_h > 0 && _w > 0)){
            return false;
        }

        const _src = this._imagesource ?? this.value;
        return typeof _src === 'string' && _src.trim().length > 0;
    }

    /**
     * Opens the crop modal for the image currently shown (preview or stored value).
     * On confirm, replaces the value with the cropped data URL and closes the config panel.
     */
    async OnCropCurrent(): Promise<void> {
        if (!this.CanCrop){
            return;
        }

        const _src = (this._imagesource ?? this.value) as string;
        const _result = await this._showCropModal(_src);
        if (_result === null){
            this.change.markForCheck();
            return;
        }

        this.ConfigForm.get('mode')?.setValue('upload');
        this._iswebresult = false;
        this._imagesource = _result;
        this.value = _result;
        this.OnHideConfig();
        this.change.markForCheck();
    }

    /**
     * Indicates whether a fetched search image can be applied.
     */
    get SearchReady(): boolean {
        return this.ConfigForm.get('mode')?.value === 'search' && this._iswebresult && this._imagesource !== null;
    }

    /**
     * Indicates whether config text is non-empty and valid.
     */
    private get _ValidText(): boolean {
        const _text = String(this.ConfigForm.get('text')?.value || '').trim();
        if (_text.length === 0){
            return false;
        }

        return !!this.ConfigForm.get('text')?.valid;
    }

    /**
     * Indicates whether both hex color controls are valid.
     */
    private get _ValidColor(): boolean {
        return !!this.ConfigForm.get('bgHex')?.valid && !!this.ConfigForm.get('fgHex')?.valid;
    }

    private _imagesource: string | null = null;
    private _searchquery = '';
    private _searchretry = 0;
    private _iswebresult = false;

    private _showconfig = false;
    get ShowConfig(): boolean {
        return this._showconfig;
    }
    set ShowConfig(value: boolean){
        if (this._showconfig !== value){
            this._showconfig = value;
            this.change.markForCheck();
        }
    }

    private _configForm: FormGroup | null = null;
    get ConfigForm(): FormGroup {
        if (this._configForm == null){
            this._configForm = this.LoadConfigForm();
        }
        return this._configForm;
    }

    private LoadConfigForm(): FormGroup {
        const _mode: UppIImageSource = 'avatar';
        const _bgHex = '5663ff';
        const _fgHex = 'ffffff';
        const _bgColor: string = '#' + _bgHex;
        const _fgColor: string = '#' + _fgHex;
        const _text = String(this.search ?? '');
        const _avatar: UppAvatarMode = 'min';

        let _form: FormGroup | null = this._configForm;
        if (_form == null){
            _form = new FormGroup({
                mode: new FormControl(_mode, [
                    Validators.required
                ]),
                bgColor: new FormControl(_bgColor, [
                    // no validators required
                ]),
                fgColor: new FormControl(_fgColor, [
                    // no validators required
                ]),
                bgHex: new FormControl(_bgHex, [
                    Validators.required,
                    Validators.pattern(/^[a-fA-F0-9]{6}$/)
                ]),
                fgHex: new FormControl(_fgHex, [
                    Validators.required,
                    Validators.pattern(/^[a-fA-F0-9]{6}$/)
                ]),
                text: new FormControl(_text, [
                    Validators.maxLength(99)
                ]),
                avatarMode: new FormControl(_avatar, [
                    Validators.required
                ])
            });
        }

        _form.markAsPristine();
        setTimeout(() => {
            _form?.markAsPristine();
        }, 0);

        this.change.markForCheck();

        return _form;
    }

    /**
     * Toggles the configuration panel visibility.
     * @param event Optional UI event to prevent default propagation.
     */
    OnShowConfig(event: Event | null = null): void {
        if (event){
            event.preventDefault();
            event.stopPropagation();
        }

        this.ShowConfig = !this.ShowConfig;

        if (this.ShowConfig){
            const _text = String(this.search ?? '');
            const _control = this.ConfigForm.get('text');
            if (_control && _control.value !== _text){
                _control.setValue(_text, { emitEvent: false });
                _control.markAsPristine();
            }

            this._imagesource = this.value;
            this._iswebresult = false;
        }
    }

    /**
     * Closes the configuration panel if it is open.
     */
    OnHideConfig(): void {
        if (!this.ShowConfig){
            return;
        }

        this.ShowConfig = false;
    }

    /**
     * Accepts the configured image value and closes the panel.
     * @param value New image source to apply, or `null` to clear.
     */
    OnConfigAccept(value: string | null): void {
        this.value = value ?? null;
        this.ShowConfig = false;
    }

    /**
     * Cancels the configuration flow and closes the panel.
     */
    OnConfigCancel(): void {
        this.ShowConfig = false;
    }

    /**
     * Opens the local file picker for config upload mode.
     */
    OnConfigImageClick() {
        if (this.configUploader?.nativeElement){
            (this.configUploader.nativeElement as HTMLInputElement).click();
        }
    }

    /**
     * Processes a selected local image in config mode.
     * @param event Change event emitted by the hidden file input.
     */
    async OnConfigImageChange(event: Event) {
        const input = event.target as HTMLInputElement;
        const _file = input.files?.[0] || null;
        if (_file){
            const _dataUrl = await this._readImage(_file);
            const _result = await this._showCropModal(_dataUrl);
            if (_result === null){
                input.value = '';
                this.change.markForCheck();
                return;
            }

            this.ConfigForm.get('mode')?.setValue('upload');
            
            this._iswebresult = false;
            this._imagesource = _result;
            this.value = _result;
        }

        this.OnHideConfig();
        input.value = '';
        this.change.markForCheck();
    }

    OnAvatar() {
        if (!this.CanAvatar){
            return;
        }

        const _text = String(this.ConfigForm.get('text')?.value || '').trim();
        if (_text === ''){
            return;
        }
        const _avatarMode: UppAvatarMode = this.ConfigForm.get('avatarMode')?.value || 'min';

        const _background = this._hexColor(String(this.ConfigForm.get('bgHex')?.value || ''));
        const _foreground = this._hexColor(String(this.ConfigForm.get('fgHex')?.value || ''));
        const _defaultBgHex = '5663ff';
        const _defaultFgHex = 'ffffff';

        const _params = new URLSearchParams({
            size: '512',
            background: (_background.length === 6) ? _background : _defaultBgHex,
            color: (_foreground.length === 6) ? _foreground : _defaultFgHex,
        });

        if (_avatarMode === 'full'){
            _params.set('text', _text);
        }
        else {
            _params.set('name', _text);
        }

        const _endpoint = (_avatarMode === 'full') ? 'photo/text' : 'photo/avatar';
        this.ConfigForm.get('mode')?.setValue('avatar');
        this._imagesource = `${AppConstants.baseURL}${_endpoint}?${_params.toString()}`;
        this.value = this._imagesource;

        this.OnHideConfig();
    }

    /**
     * Requests an image from the internet search backend.
     */
    async OnSearch() {
        if (!this.CanSearch){
            return;
        }

        const _query = String(this.ConfigForm.get('text')?.value || '').trim();
        if (_query !== this._searchquery){
            this._searchquery = _query;
            this._searchretry = 0;
        }
        const _retry = this._searchretry;
        this._searchretry++;

        await this.toast.ShowWait();
        try {
            const _response: any = await this.adhoc.DoRequest(
                'photo/search',
                { q: encodeURIComponent(_query), retry: String(_retry) },
                null,
                false,
                null,
                'blob'
            );

            const _source = await this._toImageSrc(_response);
            if (!_source){
                this._iswebresult = false;
                this.toast.ShowAlert('warning', this.tr('@image_config_search_no_results'), 8000);
                return;
            }

            this.ConfigForm.get('mode')?.setValue('search');
            this._imagesource = _source;
            this._iswebresult = true;
            this._source = _source;
            this.change.markForCheck();
        }
        catch (error){
            this._iswebresult = false;
            const _messageKey = this._IsSearchTimeout(error)
                ? '@image_config_search_timeout'
                : '@image_config_search_error';
            this.toast.ShowAlert('danger', this.tr(_messageKey), 8000);
            console.error('[upp-image] internet image search error', error);
        }
        finally {
            this.toast.HideWait();
        }
    }

    /**
     * Detects client/gateway timeouts so the toast can explain the delay.
     */
    private _IsSearchTimeout(error: unknown): boolean {
        const _status = (error as { status?: number } | null)?.status;
        if (_status === 0 || _status === 408 || _status === 502 || _status === 504){
            return true;
        }

        const _name = String((error as { name?: string } | null)?.name || '');
        if (_name === 'TimeoutError'){
            return true;
        }

        const _message = String((error as { message?: string } | null)?.message || error || '');
        return /timeout/i.test(_message);
    }

    /**
     * Applies and crops the current internet search result.
     */
    async OnUseSearch(): Promise<void> {
        if (this._imagesource === null){
            return;
        }

        const _result = await this._showCropModal(this._imagesource);
        if (_result === null){
            this.change.markForCheck();
            return;
        }

        this.ConfigForm.get('mode')?.setValue('search');
        this._imagesource = _result;
        this._source = _result;
        this.value = _result;
        this.OnHideConfig();
    }

    /**
     * Removes the current image and closes the config panel.
     */
    OnDelete() {
        this._imagesource = null;
        this._iswebresult = false;
        this._cachesrc = null;
        this.value = null;
        this.OnHideConfig();
    }

    /**
     * Updates color controls from a typed hexadecimal value.
     * @param kind Which color control to update.
     * @param value Raw input value from the UI.
     */
    OnColorChanged(kind: 'background' | 'foreground', value: string | null) {
        const _hex = this._hexColor(value);
        if (kind === 'background'){
            this.ConfigForm.get('bgHex')?.setValue(_hex);
            if (_hex.length === 6){
                this.ConfigForm.get('bgColor')?.setValue(`#${_hex}`);
            }
        }
        else if (kind === 'foreground') {
            this.ConfigForm.get('fgHex')?.setValue(_hex);
            if (_hex.length === 6){
                this.ConfigForm.get('fgColor')?.setValue(`#${_hex}`);
            }
        }

        this.change.markForCheck();
    }

    private _hexColor(value: string | null): string {
        return String(value || '')
            .replace(/#/g, '')
            .replace(/[^a-fA-F0-9]/g, '')
            .slice(0, 6)
            .toLowerCase();
    }

    private async _toImageSrc(response: Blob | null | undefined): Promise<string | null> {
        if (response === null || typeof response === 'undefined'){
            return null;
        }

        if (!(response instanceof Blob)){
            return null;
        }

        const _type = String(response.type || '').toLowerCase();
        if (_type.startsWith('image/')){
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve((typeof reader.result === 'string') ? reader.result : null);
                };
                reader.onerror = () => {
                    resolve(null);
                };
                reader.readAsDataURL(response);
            });
        }

        const _raw = (await response.text()).trim();
        if (_raw === '' || _raw === 'null'){
            return null;
        }

        return null;
    }

    /************************************************/
    /* adjust image according to exif information   */
    /************************************************/

    // https://stackoverflow.com/questions/7584794/accessing-jpeg-exif-rotation-data-in-javascript-on-the-client-side/32490603#32490603

    /**
     * Handles the EXIF orientation of an image.
     * Extracts the orientation value from the image's EXIF data.
     * @param event The file load event containing the image data.
     * @returns The orientation value or an error code (`-2` for non-JPEG, `-1` for undefined).
     * @private
     */    
    private _getOrientation(event: any) {
        const view = new DataView(event.target.result);
        if (view.getUint16(0, false) != 0xFFD8) {
            return -2;  // not a JPEG
        }

        const length = view.byteLength;
        let offset = 2;
        while (offset < length) {
            if (view.getUint16(offset+2, false) <= 8){
                return -1;  // not defined
            }
            
            const marker = view.getUint16(offset, false);
            offset += 2;
            if (marker == 0xFFE1) {
                if (view.getUint32(offset += 2, false) != 0x45786966) {
                    return -1;  // not defined
                }

                const little = view.getUint16(offset += 6, false) == 0x4949;
                offset += view.getUint32(offset + 4, little);
                const tags = view.getUint16(offset, little);
                offset += 2;
                for (let i = 0; i < tags; i++) {
                    if (view.getUint16(offset + (i * 12), little) == 0x0112) {
                        return view.getUint16(offset + (i * 12) + 8, little);
                    }
                }
            }
            else if ((marker & 0xFF00) != 0xFF00) {
                break;
            }
            else { 
                offset += view.getUint16(offset, false);
            }
        }
        
        return -1;  // not defined
    };

    // https://github.com/mshibl/Exif-Stripper/blob/master/exif-stripper.js

    /**
     * Removes EXIF metadata from the provided image buffer.
     * Extracts the image data without metadata for security and size efficiency.
     * @param event The file load event containing the image buffer.
     * @returns A Blob containing the image data without EXIF metadata, or `null` if failed.
     * @private
     */    
    private _removeExif(event: any){
        const imageArrayBuffer = event.target.result;
        const dv = new DataView(imageArrayBuffer);
        
	    const pieces = [];
	    let offset = 0, recess = 0, i = 0;
	    if (dv.getUint16(offset) == 0xffd8){
	        offset += 2;
	        let app1 = dv.getUint16(offset);
	        offset += 2;
	        while (offset < dv.byteLength){
	            if (app1 == 0xffe1){
	                pieces[i] = {recess:recess,offset:offset-2};
	                recess = offset + dv.getUint16(offset);
	                i++;
	            }
	            else if (app1 == 0xffda){
	                break;
	            }
	            offset += dv.getUint16(offset);
	            app1 = dv.getUint16(offset);
	            offset += 2;
	        }
	        if (pieces.length > 0){
	            const newPieces = [];
	            pieces.forEach(function(v){
	                newPieces.push(imageArrayBuffer.slice(v.recess, v.offset));
	            }, this);
	            newPieces.push(imageArrayBuffer.slice(recess));
                
                return new Blob(newPieces, {type: 'image/jpeg'});
	        }
	    }

        return null;
	}    
    
    // https://stackoverflow.com/questions/20600800/js-client-side-exif-orientation-rotate-and-mirror-jpeg-images    

    /**
     * Applies transformations to an image based on EXIF orientation data.
     * Ensures the image is correctly rotated or flipped according to the metadata.
     * @param callback A callback to handle the transformed image's data URL.
     * @param orientation The EXIF orientation value.
     * @param image The image element to be transformed.
     * @private
     */    
    private _imgTransform(callback: (dataUrl: string) => void, orientation: number, image: HTMLImageElement){
        const canvas = document.createElement('canvas');

        let w = image.width
        let h = image.height; 

        const context = canvas.getContext('2d');            
        if (context){
            const max = 1024;   // max side: 1024 pixels
            
            let scale = 1; 
            if ((w > h) && (w > max)){
                scale = max/w;
            }
            if ((h >= w) && (h > max)){
                scale = max/h;
            }
    
            w = Math.round(w * scale);
            h = Math.round(h * scale);
        
            if ((orientation > 4) && (orientation < 9)) {
                canvas.width = h;
                canvas.height = w;
            } 
            else {
                canvas.width = w;
                canvas.height = h;
            }
            
            let tm = [ 1, 0, 0, 1, 0, 0 ];  // transformation matrix        
            switch (orientation) {
                case 2: tm = [ -1, 0, 0, 0, image.width, 0 ]; break;
                case 3: tm = [ -1, 0, 0, -1, image.width, image.height ]; break;
                case 4: tm = [ 1, 0, 0, -1, 0, image.height ]; break;
                case 5: tm = [ 0, 1, 1, 0, 0, 0 ]; break;
                case 6: tm = [ 0, 1, -1, 0, image.height, 0 ]; break;
                case 7: tm = [ 0, -1, -1, 0, image.height, image.width ]; break;
                case 8: tm = [ 0, -1, 1, 0, 0, image.width ]; break;
            }
            
            context.transform(scale, 0, 0, scale, 0, 0);
            context.transform(tm[0], tm[1], tm[2], tm[3], tm[4], tm[5]);        
            
            context.drawImage(image, 0, 0);
        }

        callback(canvas.toDataURL());    
    }
        
    /**
     * Reads an image file and processes it, applying EXIF-based transformations and removing metadata.
     * @param file The image file to be processed.
     * @returns A promise that resolves to the processed image's data URL.
     * @private
     */    
    private async _readImage(file: File): Promise<string> {
        const removeExifAndTransform = async (arrayBuffer: ArrayBuffer, orientation: number): Promise<string> => {
            const blob = this._removeExif({ target: { result: arrayBuffer } });
            const image = new Image();
    
            return new Promise((resolve) => {
                image.onload = () => {
                    this._imgTransform((dataURL) => resolve(dataURL), orientation, image);
                    URL.revokeObjectURL(image.src);
                };
    
                image.src = URL.createObjectURL(blob || new Blob([arrayBuffer], { type: "image/jpeg" }));
            });
        };
    
        const getOrientation = (arrayBuffer: ArrayBuffer): number => {
            return this._getOrientation({ target: { result: arrayBuffer } });
        };
    
        const fileArrayBuffer = await file.arrayBuffer();
        const orientation = getOrientation(fileArrayBuffer);
    
        return removeExifAndTransform(fileArrayBuffer, orientation);
    }

    /********************************************/
    /* get/set accesors                         */
    /********************************************/
    
    @Input()

    /**
     * Gets the current value of the image.
     * 
     * @remarks
     * The `value` accessor retrieves the image's base64 data (`_value`) if available.
     * Otherwise, it falls back to the `src` property, which can be a URL or base64 string.
     * 
     * @returns The base64 representation or the source URL of the image.
     */    
    get value(): string | null {
        return this._value || this.src;
    }

    /**
     * Sets a new value for the image.
     * 
     * @remarks
     * The `value` accessor updates the internal `_value` and `src` properties when the
     * provided value differs from the current `src`. It also triggers change detection,
     * invokes the registered `ControlValueAccessor` callbacks, and emits the `Changed` event.
     * 
     * @param v The new value for the image. This can be a base64 string or a URL.
     */    
    set value(v: string | null) {
        if (v !== this.src) {
            this._value = v;

            this._onChangeCallback(v);
            this._onTouchCallback();

            this.Changed.emit();
        }
    }    
    
    /********************************************/
    /* ControlValueAccessor                     */
    /********************************************/
    
    /**
     * Implements `ControlValueAccessor` to handle form binding.
     */    
    writeValue(value: string | null) {
        if (value !== this.src) {
            this.value = value;
        }
    }

    /**
     * Registers a callback for when the value changes.
     */    
    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }

    /**
     * Registers a callback for when the control is touched.
     */    
    registerOnTouched(fn: any) {
        this._onTouchCallback = fn;
    } 
};
